import React, { useState, useEffect } from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { X, Home, Sunrise, Moon, Repeat, Feather } from 'lucide-react';
import { gerarJornada } from '../utils/jornadaEngine';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COR = { Peregrino:'#1B4FD8', Guardião:'#C084FC', Amante:'#EC4899', Alquimista:'#F59E0B' };
const ROOT_LABEL = { peregrino:'Peregrino', guardiao:'Guardião', amante:'Amante', alquimista:'Alquimista' };

// A Linha do Tempo do seu Coração (modal). Uso: <JornadaNoTempo email={email} nome={nome} onClose={...} />
const JornadaNoTempo = ({ email, nome, onClose }) => {
  const [estado, setEstado] = useState('carregando'); // carregando | poucos | pronto | erro
  const [j, setJ] = useState(null);

  useEffect(() => {
    if (!email) { setEstado('poucos'); return; }
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/test-results/evolucao/${encodeURIComponent(email)}`);
        if (!res.ok) { setEstado('poucos'); return; }
        const hist = await res.json();
        const jornada = gerarJornada(hist, { nome });
        if (!jornada.suficiente) { setEstado('poucos'); return; }
        setJ(jornada); setEstado('pronto');
      } catch (e) { setEstado('erro'); }
    })();
  }, [email, nome]);

  const Overlay = ({ children }) => (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-gradient-to-br from-slate-900 to-indigo-950 rounded-2xl border border-indigo-500/20 max-w-2xl w-full my-8 relative" data-testid="jornada-no-tempo">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/40 hover:text-white z-10" style={{ border:'none', background:'transparent' }}><X className="w-5 h-5" /></button>
        {children}
      </div>
    </div>
  );

  if (estado === 'carregando') return <Overlay><div className="p-10 text-center"><div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" /><p className="text-white/70">Reunindo as suas estações…</p></div></Overlay>;
  if (estado === 'erro') return <Overlay><div className="p-10 text-center"><p className="text-white/70">Não foi possível carregar a sua jornada agora.</p></div></Overlay>;
  if (estado === 'poucos') return (
    <Overlay><div className="p-8 text-center">
      <Feather className="w-12 h-12 text-amber-400/60 mx-auto mb-4" />
      <h3 className="text-white text-lg font-bold mb-2">A sua jornada está só começando</h3>
      <p className="text-white/60 text-sm leading-relaxed mb-6">Assim que você refizer o teste algumas vezes (usando o mesmo e-mail), eu vou desenhar aqui a linha do tempo do seu coração — as estações que mudam, despertam e descansam ao longo da sua vida.</p>
      <button onClick={onClose} className="px-6 py-2.5 bg-white/10 text-white rounded-xl hover:bg-white/20 text-sm" style={{ border:'none' }}>Entendi</button>
    </div></Overlay>
  );

  // pronto
  const chartData = j.pontos.map(p => ({ label: p.label, Peregrino: p.pcts.peregrino, Guardião: p.pcts.guardiao, Amante: p.pcts.amante, Alquimista: p.pcts.alquimista }));

  return (
    <Overlay>
      <div className="p-6 md:p-7 space-y-6">
        <div className="text-center">
          <h3 className="text-2xl font-extrabold text-white">A Linha do Tempo do seu Coração</h3>
          <p className="text-white/50 text-sm mt-1">{j.nTestes} momentos · ao longo de {j.periodoTexto}</p>
        </div>

        {/* Gráfico */}
        <div className="bg-white/[0.04] rounded-2xl p-3 border border-white/10" style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="label" tick={{ fill:'rgba(255,255,255,0.5)', fontSize:11 }} />
              <YAxis domain={[0,100]} tick={{ fill:'rgba(255,255,255,0.4)', fontSize:11 }} />
              <Tooltip contentStyle={{ background:'#1e1b4b', border:'1px solid rgba(255,255,255,0.15)', borderRadius:12, color:'#fff' }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {Object.keys(COR).map(k => <Line key={k} type="monotone" dataKey={k} stroke={COR[k]} strokeWidth={2.5} dot={{ r:3 }} activeDot={{ r:5 }} />)}
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Insights */}
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <p className="flex items-center gap-2 text-amber-200 text-sm font-semibold mb-1"><Home className="w-4 h-4" /> Sua estação-morada</p>
            <p className="text-white/75 text-sm">O <b>{j.morada.nome}</b> — a estação que mais habitou você (média de {j.morada.media}%). É a sua casa interior.</p>
          </div>
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <p className="flex items-center gap-2 text-cyan-200 text-sm font-semibold mb-1"><Repeat className="w-4 h-4" /> A mais móvel</p>
            <p className="text-white/75 text-sm">O <b>{j.volatil.nome}</b> foi a estação que mais variou — a que mais traduz os seus altos e baixos.</p>
          </div>
          {j.despertaram.length > 0 && (
            <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/20">
              <p className="flex items-center gap-2 text-emerald-300 text-sm font-semibold mb-1"><Sunrise className="w-4 h-4" /> Despertaram</p>
              <p className="text-white/75 text-sm">{j.despertaram.map(d=>d.nome).join(', ')} cresceram ao longo do caminho.</p>
            </div>
          )}
          {j.silenciaram.length > 0 && (
            <div className="bg-slate-500/10 rounded-xl p-4 border border-slate-500/20">
              <p className="flex items-center gap-2 text-slate-300 text-sm font-semibold mb-1"><Moon className="w-4 h-4" /> Recolheram-se</p>
              <p className="text-white/75 text-sm">{j.silenciaram.map(d=>d.nome).join(', ')} pedem atenção renovada.</p>
            </div>
          )}
        </div>

        {/* Carta da Travessia */}
        <div className="rounded-2xl p-6 border border-amber-500/20" style={{ background:'linear-gradient(160deg, rgba(69,26,3,0.5), rgba(27,21,48,0.6))' }}>
          <p className="text-amber-100/90 text-lg italic mb-4" style={{ fontFamily:"'Playfair Display', serif" }}>{j.carta.saudacao}</p>
          <div className="space-y-3" style={{ fontFamily:"'Lora', Georgia, serif" }}>
            {j.carta.blocos.map((b,i)=>(<p key={i} className="text-white/85 leading-relaxed text-[1.02rem]">{b}</p>))}
          </div>
          <p className="text-center text-white text-xl italic mt-5" style={{ fontFamily:"'Playfair Display', serif" }}>“{j.carta.semente}”</p>
          <p className="text-right text-white/70 italic mt-3" style={{ fontFamily:"'Lora', Georgia, serif" }}>{j.carta.assinatura}</p>
        </div>

        <div className="text-center">
          <button onClick={onClose} className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 text-sm" style={{ border:'none' }}>Voltar ao resultado</button>
        </div>
      </div>
    </Overlay>
  );
};
export default JornadaNoTempo;
